import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AnimationShell, Container } from '@/components';
import { Card } from '@/components/ui/card';
import {
  AboutCard,
  CompanyForm,
  CompanyFormFields,
  InvoicingCard,
  ProfileHeaderCard,
  TeamCard,
  useGetCompanyQuery,
  useUpdateCompanyMutation,
} from '@/features/company';
import { useBreadcrumbs } from '@/features/header';
import { useNotifications } from '@/features/notifications';
import { useGetCurrentUserQuery } from '@/features/users';
import { useRegisterGAPageView } from '@/hooks';

export function CompanySettingsPage() {
  const { t } = useTranslation(['settings']);

  useBreadcrumbs(() => [{ title: t(`Company settings`) }]);
  useRegisterGAPageView('Company settings');

  const { data: user } = useGetCurrentUserQuery();
  const { data: company } = useGetCompanyQuery(user ? { id: user.company.id } : skipToken);
  const { add } = useNotifications();

  const [updateCompany] = useUpdateCompanyMutation();

  const onCompanySubmit = useCallback(
    (formData: CompanyFormFields) => {
      if (!user) return;

      const { location_interests } = formData;
      const formattedLocationInterests = Array.isArray(location_interests)
        ? location_interests.map((item) => item.trim())
        : location_interests?.split(',').map((item) => item.trim()) || [];

      // Only interests: the profile (name/website/logo/description) is determined by ProfileHeaderCard.
      updateCompany({
        id: user.company.id,
        data: {
          activity_interests: formData.activity_interests,
          technology_interests: formData.technology_interests,
          other_technology: formData.other_technology,
          countries_interests: formData.countries_interests,
          location_interests: formattedLocationInterests,
          modal_interests: formData.modal_interests,
          bess_size: formData.bess_size,
          development_status_interests: formData.development_status_interests,
          solar_size: formData.solar_size,
          solar_development_status: formData.solar_development_status,
        },
      })
        .unwrap()
        .then(() => {
          add({
            id: new Date().toString(),
            title: t('Company updated'),
            description: t('Your company has been updated'),
          });
        })
        .catch(() => {
          add({
            id: new Date().toString(),
            title: t('ERROR'),
            description: t('There was an error updating your company. Please try again later.'),
          });
        });
    },

    [user, updateCompany, add, t]
  );

  return (
    <Container className="space-y-12">
      <AnimationShell element="article" type="opacity" duration={0} className="mt-4 h-auto overflow-hidden">
        <div className="space-y-6 p-2 pb-12">
          {company ? (
            <>
              <ProfileHeaderCard company={company} />
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.7fr_1fr]">
                <div className="space-y-6">
                  <Card className="p-6">
                    <CompanyForm company={company} onSubmit={onCompanySubmit} />
                  </Card>
                  <AboutCard company={company} />
                </div>
                <div className="space-y-6">
                  <TeamCard />
                  <InvoicingCard company={company} />
                </div>
              </div>
            </>
          ) : null}
        </div>
      </AnimationShell>
    </Container>
  );
}
